<script setup lang="ts">
import type { NativeDateInput } from '#layers/base/public';
import { useClipboard } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { formatPreciseInstantWithTimeZone, formatTimezoneLabel, formatUnixTimestampSeconds, resolveTimezone, TIMEZONE_LOCAL, TIMEZONE_UTC, toInstant, useUserLocale } from '#layers/base/public';

interface TimestampHoverCardRow {
	id: string;
	icon: string;
	label: string;
	detail?: string;
	value: string;
	mono?: boolean;
}

const props = defineProps<{
	instant: NativeDateInput;
	localTimezone?: string;
	/** Adds the precise time for screen readers, for triggers whose visible text doesn't state it. */
	includeScreenReaderDescription?: boolean;
}>();

const { formatLocale } = useUserLocale();
const { t } = useI18n();
const appConfig = useAppConfig();
const { copy, copied } = useClipboard();

const copiedId = ref<string>();

function copyRow(row: TimestampHoverCardRow): void {
	copy(row.value);
	copiedId.value = row.id;
}

const instant = computed(() => toInstant(props.instant));
const localTimezone = computed(() => resolveTimezone(props.localTimezone ?? TIMEZONE_LOCAL));
const localDateTime = computed(() => instant.value.toZonedDateTimeISO(localTimezone.value).toPlainDateTime());
const localTimezoneLabel = computed(() => formatTimezoneLabel(localTimezone.value, {
	locale: formatLocale.value,
	dateTime: localDateTime.value,
}));

const localValue = computed(() => formatPreciseInstantWithTimeZone(instant.value, {
	timeZone: localTimezone.value,
	locale: formatLocale.value,
}));
const utcValue = computed(() => formatPreciseInstantWithTimeZone(instant.value, {
	timeZone: TIMEZONE_UTC,
	locale: formatLocale.value,
}));

const rows = computed<TimestampHoverCardRow[]>(() => [
	{
		id: 'local',
		icon: 'lucide:monitor',
		label: t('time.local'),
		detail: localTimezoneLabel.value,
		value: localValue.value,
	},
	{
		id: 'utc',
		icon: 'lucide:globe',
		label: t('time.utc'),
		value: utcValue.value,
	},
	{
		id: 'unix',
		icon: 'lucide:terminal',
		label: t('time.unix'),
		value: formatUnixTimestampSeconds(instant.value),
		mono: true,
	},
]);

// The card is pointer-only, so screen readers can't reach it.
const screenReaderDescription = computed(() => t('time.precise_local_and_utc', {
	timezone: localTimezoneLabel.value,
	local: localValue.value,
	utc: utcValue.value,
}));
</script>

<template>
	<DHoverCard
		:content="{ side: 'top', align: 'center', sideOffset: 4 }"
		content-class="w-96 max-w-[calc(100vw-2rem)]"
	>
		<slot />

		<template #content>
			<div class="space-y-1 p-3" data-testid="timestamp-hover-card">
				<button
					v-for="row in rows"
					:key="row.id"
					type="button"
					class="
						group grid w-full grid-cols-[auto_minmax(5rem,auto)_minmax(0,1fr)_auto] items-center gap-2 rounded-sm p-1
						text-start transition-colors
						hover:bg-elevated
					"
					:aria-label="`${t('common.copy_to_clipboard')}: ${row.label}, ${row.value}`"
					:data-testid="`timestamp-hover-card-row-${row.id}`"
					@click="copyRow(row)"
				>
					<DIcon :name="row.icon" class="size-4 text-dimmed transition-colors group-hover:text-default" />
					<span class="flex min-w-0 items-baseline gap-1">
						<span class="text-sm font-medium text-default transition-colors group-hover:text-highlighted">{{ row.label }}</span>
						<span v-if="row.detail" class="text-sm text-muted">{{ row.detail }}</span>
					</span>
					<span class="min-w-0 truncate text-end text-sm text-default" :class="{ 'font-mono': row.mono }">
						{{ row.value }}
					</span>
					<DIcon
						:name="copied && copiedId === row.id ? appConfig.ui.icons.check : 'lucide:copy'"
						class="size-4 text-dimmed"
					/>
				</button>
			</div>
		</template>
	</DHoverCard>
	<span v-if="includeScreenReaderDescription" class="sr-only" data-testid="timestamp-screen-reader-description">{{ screenReaderDescription }}</span>
</template>
